/**
 * BilanCategoryTable Component
 *
 * Displays categories in a table format for accounting statements.
 */

import { useTranslation } from 'react-i18next';
import { AccountingStatementCategory } from '@budgira/shared';
import { cn } from '../../utils/cn';

interface BilanCategoryTableProps {
  categories: AccountingStatementCategory[];
  type: 'debit' | 'credit';
  total: number;
  title: string;
}

export const BilanCategoryTable = ({
  categories,
  type,
  total,
  title,
}: BilanCategoryTableProps) => {
  const { i18n } = useTranslation();

  const formatAmount = (amount: number) => {
    return amount.toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: 'EUR',
    });
  };

  const filteredCategories = categories.filter((c) => c.baseType === type);

  if (filteredCategories.length === 0) {
    return null;
  }

  const isExpense = type === 'debit';
  const colorClass = isExpense
    ? 'text-red-600 dark:text-red-400'
    : 'text-green-600 dark:text-green-400';
  const bgClass = isExpense
    ? 'bg-red-50 dark:bg-red-900/20'
    : 'bg-green-50 dark:bg-green-900/20';
  const borderClass = isExpense
    ? 'border-red-200 dark:border-red-800'
    : 'border-green-200 dark:border-green-800';

  return (
    <div className={cn('rounded-xl border', borderClass, bgClass)}>
      <div className={cn('px-4 py-3 border-b', borderClass)}>
        <h3 className={cn('font-semibold', colorClass)}>{title}</h3>
      </div>
      <div className="p-4">
        <table className="w-full">
          <tbody>
            {filteredCategories.map((category) => (
              <tr
                key={category.id}
                className="border-b border-slate-200/50 dark:border-slate-700/50 last:border-0"
              >
                <td className="py-2 text-sm text-slate-700 dark:text-slate-300">
                  {category.categoryName}
                </td>
                <td className={cn('py-2 text-sm text-right font-medium', colorClass)}>
                  {isExpense ? '-' : '+'}
                  {formatAmount(category.amount)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-300 dark:border-slate-600">
              <td className={cn('py-3 font-semibold', colorClass)}>Total</td>
              <td className={cn('py-3 text-right font-bold', colorClass)}>
                {isExpense ? '-' : '+'}
                {formatAmount(total)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default BilanCategoryTable;
